import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1770745560263-a8fc696de90b?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000";
const POLO_MOCKUP =
  "https://customer-assets.emergentagent.com/job_custom-team-jerseys-1/artifacts/cnhyrmrv_Samoan%20Church%20Uniforms.png";

const BULLETS = [
  "Orders from 10+",
  "Samoan, Tongan & Cook Islands designs",
  "Custom logos and names",
  "Auckland-based service",
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-32 overflow-hidden"
    >
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_BG}
          alt="Samoan Tongan Cook Islands church choir in custom Pacific Heritage Uniforms — Auckland NZ studio"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060d24] via-[#0a1530]/90 to-[#0a1530]" />
        <div className="absolute inset-0 tribal-pattern opacity-70" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 scroll-fade-in">
          <div
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 border border-gold bg-gold/10 px-3 py-1.5 mb-6"
            style={{ background: "rgba(212,175,55,0.08)" }}
          >
            <Sparkles size={14} className="text-gold" />
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">
              Pacific Heritage Uniform Specialists · Auckland NZ
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display uppercase font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[0.95]"
          >
            Wear Your <span className="text-teal-400">Heritage</span>. <br />
            Represent Your <span className="text-gold">Community</span>.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-6 text-zinc-300 text-base md:text-xl max-w-2xl leading-relaxed"
          >
            Custom <strong className="text-white">Samoan, Tongan and Cook Islands</strong> polo
            uniforms for churches, ministries, choirs, conferences and reunion groups.
          </p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-3 max-w-xl" data-testid="hero-bullets">
            {BULLETS.map((b) => (
              <li key={b} className="flex items-start gap-3 text-zinc-200">
                <span className="mt-1 w-5 h-5 bg-teal-400/15 border border-teal-400/40 grid place-items-center shrink-0">
                  <Check size={12} className="text-teal-400" />
                </span>
                <span className="text-sm font-medium">{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-mockup"
              className="btn-primary inline-flex items-center gap-2"
            >
              Get Free Design Mockup
              <ArrowRight size={18} />
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-quote"
              className="btn-secondary inline-flex items-center gap-2"
            >
              Request Quote
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-3 text-xs uppercase tracking-wider">
            <Link to="/samoan-church-uniforms" className="text-zinc-400 hover:text-teal-400">
              Samoan Church Uniforms →
            </Link>
            <span className="text-zinc-700">·</span>
            <Link to="/tongan-church-uniforms" className="text-zinc-400 hover:text-teal-400">
              Tongan Designs →
            </Link>
            <span className="text-zinc-700">·</span>
            <Link to="/cook-islands-uniforms" className="text-zinc-400 hover:text-teal-400">
              Cook Islands →
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5 relative" data-testid="hero-mockup-column">
          <div className="relative aspect-[4/5] max-w-md mx-auto">
            <div className="absolute -inset-6 bg-teal-400/15 blur-3xl" />
            <div className="relative h-full w-full border border-zinc-800 bg-zinc-900 overflow-hidden">
              <img
                src={POLO_MOCKUP}
                alt="Samoan Church Uniforms — custom Pacific Heritage Polo Shirts with red lei and tatau patterns, group photo Auckland NZ"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#060d24] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-gold">
                    Church Unity Collection™
                  </div>
                  <div className="font-display text-xl uppercase text-white mt-1">
                    Heritage Polo 01
                  </div>
                </div>
                <div className="bg-teal-400 text-zinc-950 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
                  Custom
                </div>
              </div>
            </div>

            <div className="hidden md:block absolute -left-6 top-10 bg-zinc-900 border border-gold/50 px-4 py-3 shadow-2xl">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">From</div>
              <div className="font-display text-2xl text-gold">$49</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">per polo</div>
            </div>

            <div className="hidden md:block absolute -right-4 bottom-16 bg-zinc-900 border border-zinc-800 px-4 py-3 shadow-2xl">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Min Order</div>
              <div className="font-display text-lg text-white">10 polos</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
